import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Ban, AlertCircle } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { UserSubscription } from "@/types/subscription";

const cancelReasons = [
  "User requested cancellation",
  "Payment issues",
  "Upgrading to different plan",
  "Switching to competitor",
  "Service not needed anymore",
  "Not satisfied with service",
  "Too expensive",
  "Administrative action",
  "Other",
];

// Define the form schema
const cancelSubscriptionSchema = z.object({
  cancellationReason: z.string().min(1, "Please select a reason"),
  customReason: z.string().optional(),
  immediateEffect: z.boolean().default(false),
  issueRefund: z.boolean().default(false),
  refundAmount: z
    .number()
    .min(0, "Refund amount cannot be negative")
    .optional()
    .nullable(),
  adminNotes: z.string().optional(),
});

type CancelSubscriptionFormValues = z.infer<typeof cancelSubscriptionSchema>;

interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (data: CancelSubscriptionFormValues) => void;
  subscription: UserSubscription;
  isProcessing: boolean;
  userName: string;
}

export default function CancelSubscriptionDialog({
  open,
  onOpenChange,
  onConfirm,
  subscription,
  isProcessing,
  userName,
}: CancelSubscriptionDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  // Initialize the form
  const form = useForm<CancelSubscriptionFormValues>({
    resolver: zodResolver(cancelSubscriptionSchema),
    defaultValues: {
      cancellationReason: "",
      customReason: "",
      immediateEffect: false,
      issueRefund: false,
      refundAmount: null,
      adminNotes: `Cancellation processed for ${userName}'s subscription to ${subscription?.subscriptionPlan?.name}`,
    },
  });

  // Handle form submission
  const onSubmit = (data: CancelSubscriptionFormValues) => {
    // If "Other" is selected, use the custom reason
    const finalData = {
      ...data,
      cancellationReason:
        data.cancellationReason === "Other" && data.customReason
          ? data.customReason
          : data.cancellationReason,
    };
    onConfirm(finalData);
  };

  // Handle reason change
  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    form.setValue("cancellationReason", value);
  };

  // Calculate prorated refund amount
  const calculateProratedRefund = () => {
    if (!subscription.subscriptionEnd) return subscription.price;

    const now = new Date();
    const endDate = new Date(subscription.subscriptionEnd);
    const startDate = new Date(subscription.subscriptionStart);

    const totalDuration = endDate.getTime() - startDate.getTime();
    const remainingTime = endDate.getTime() - now.getTime();

    if (remainingTime <= 0) return 0;

    const remainingRatio = remainingTime / totalDuration;
    return Math.round(subscription.price * remainingRatio * 100) / 100;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            Cancel Subscription
          </DialogTitle>
          <DialogDescription>
            You are about to cancel {userName}'s subscription to{" "}
            {subscription?.subscriptionPlan?.name}. This action can be reversed
            later if needed.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="cancellationReason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cancellation Reason</FormLabel>
                  <Select
                    onValueChange={(value) => handleReasonChange(value)}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a reason" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {cancelReasons.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedReason === "Other" && (
              <FormField
                control={form.control}
                name="customReason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Specify Reason</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter cancellation reason"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="immediateEffect"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Cancel Immediately</FormLabel>
                    <FormDescription>
                      Cancel right now instead of at the end of the billing
                      period
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="issueRefund"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Issue Refund</FormLabel>
                    <FormDescription>
                      Process a refund for the remaining subscription period
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={(checked) => {
                        field.onChange(checked);
                        if (checked) {
                          form.setValue(
                            "refundAmount",
                            calculateProratedRefund()
                          );
                        } else {
                          form.setValue("refundAmount", null);
                        }
                      }}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            {form.watch("issueRefund") && (
              <FormField
                control={form.control}
                name="refundAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Refund Amount ({subscription.currencyCode})
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        max={subscription.price}
                        placeholder="Enter refund amount"
                        {...field}
                        value={field.value?.toString() || ""}
                        onChange={(e) => {
                          field.onChange(
                            e.target.value === ""
                              ? null
                              : parseFloat(e.target.value)
                          );
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Suggested prorated refund: {calculateProratedRefund()}{" "}
                      {subscription.currencyCode}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="adminNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Admin Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Enter any additional notes about this cancellation"
                      className="min-h-[80px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-800 flex gap-2">
              <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">Important</p>
                <p>
                  The subscription status will be changed to "cancelled"
                  immediately.
                  {form.watch("immediateEffect")
                    ? " User access will be terminated right away."
                    : " User will retain access until the end of the billing period."}
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Confirm Cancellation"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
